import { type CustomTooltipProps } from "ag-grid-react"
import Highcharts from "highcharts"
import { HighchartsReact } from "highcharts-react-official"
import { erf } from "mathjs"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import BellCurveChart from "~/components/reusable/graphs/BellCurveChart"
import StaticBarChart from "~/components/reusable/graphs/StaticBarChart"
import { formatNumber } from "~/utils/formatting"
import { getTailwindValue } from "~/utils/tailwind"

import HeaderTooltip from "./HeaderTooltip"

export type SafetyStockTooltipValue =
  | { kind: "noSafetyStock" }
  | {
      kind: "serviceLevel"
      serviceLevel: number
      bufferUnits: number
      minimumStockLevel: number
      mu?: number
      sigma?: number
      weightedAverage?: boolean
    }
  | {
      kind: "weekCover"
      weeks: number
      bufferUnits: number
      minimumStockLevel: number
      weeklyData: { forecast: number }[]
      aggregated?: boolean
    }
  | { kind: "text"; text: string }

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg-base font-medium tabular-nums">{value}</span>
    </div>
  )
}

function Divider() {
  return <div className="border-t border-base my-1" />
}

function Description({ children }: { children: React.ReactNode }) {
  return <p className="text-fg-muted paragraph-sm">{children}</p>
}

function WeekCoverBarChart({
  weeklyData,
  aggregated = false,
}: {
  weeklyData: { forecast: number }[]
  aggregated?: boolean
}) {
  if (!aggregated) {
    const chartData = weeklyData.map((d, i) => ({
      name: `W${i + 1}`,
      value: Math.round(d.forecast * 10) / 10,
    }))
    return (
      <div className="mt-2">
        <StaticBarChart
          data={chartData}
          color={getTailwindValue("--color-green-500")}
          height={60}
          showXAxisLabels={false}
        />
        <div className="flex mt-0.5">
          {weeklyData.map((d, i) => (
            <div key={i} className="flex-1 text-center">
              <div className="text-[10px] text-fg-base tabular-nums font-medium">
                {d.forecast.toFixed(1)}
              </div>
              <div className="text-[10px] text-fg-muted">W{i + 1}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      margin: [38, 0, 4, 0],
      height: 82,
      animation: false,
      style: { fontFamily: "inherit" },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      formatter: function () {
        return `<div class="bg-base border border-base text-fg-base" style="border-radius:6px;font-size:11px;padding:2px 6px;white-space:nowrap"><b>${this.key}</b>: ${(this.y ?? 0).toFixed(1)} units</div>`
      },
      backgroundColor: "rgba(0,0,0,0)",
      borderWidth: 0,
      shadow: false,
      padding: 0,
    },
    xAxis: {
      categories: weeklyData.map((_, i) => `W${i + 1}`),
      lineWidth: 0,
      tickWidth: 0,
      labels: { enabled: false },
    },
    yAxis: { visible: false, min: 0 },
    plotOptions: {
      column: {
        borderRadius: 2,
        borderWidth: 0,
        color: getTailwindValue("--color-green-500"),
      },
    },
    series: [
      {
        type: "column",
        data: weeklyData.map((d) => Math.round(d.forecast * 10) / 10),
      },
    ],
  }

  return (
    <div className="mt-2">
      <HighchartsReact highcharts={Highcharts} options={options} />
      <div className="flex mt-0.5">
        {weeklyData.map((_, i) => (
          <div key={i} className="flex-1 text-center">
            <div className="text-[10px] text-fg-muted">W{i + 1}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function SafetyStockContent({ value }: { value: SafetyStockTooltipValue }) {
  if (value.kind === "noSafetyStock") {
    return (
      <Description>
        No safety buffer is configured. Orders are sized to meet expected demand only, with no
        cushion for variability.
      </Description>
    )
  }

  if (value.kind === "serviceLevel") {
    const { bufferUnits, minimumStockLevel, serviceLevel, mu, sigma, weightedAverage } = value
    const serviceLevelFrac = ((erf(serviceLevel / Math.sqrt(2)) as number) + 1) / 2
    const serviceLevelPct = Math.round(serviceLevelFrac * 100)
    const stockoutRisk = 100 - serviceLevelPct
    const hasCurve = mu != null && sigma != null && sigma >= 1

    return (
      <>
        <Description>
          {hasCurve ? (
            <>
              Based on a {serviceLevelPct}% service level (Z={serviceLevel.toFixed(2)}) and a demand
              variability of {formatNumber(sigma!)} units, your safety stock is{" "}
              {formatNumber(bufferUnits)} units.
            </>
          ) : (
            <>
              Extra stock held to ensure {serviceLevelPct}% of orders can be fulfilled without
              stocking out.
            </>
          )}
        </Description>

        <div className="flex flex-col gap-1.5">
          <Row
            label="Service level"
            value={weightedAverage ? `${serviceLevelPct}% (weighted avg.)` : `${serviceLevelPct}%`}
          />
          <Row label="Stockout risk" value={`${stockoutRisk}%`} />
          {sigma != null && sigma >= 1 && (
            <Row label="Demand variability (σ)" value={`${formatNumber(sigma)} units`} />
          )}
          {minimumStockLevel > 0 && (
            <Row label="Minimum stock" value={`${formatNumber(minimumStockLevel)} units`} />
          )}
        </div>

        {hasCurve && (
          <BellCurveChart
            mu={mu!}
            sigma={sigma!}
            threshold={mu! + bufferUnits}
            safetyStockLabel={`${formatNumber(bufferUnits)} units`}
          />
        )}

        <Divider />
        <Row label="Safety stock" value={`${formatNumber(bufferUnits)} units`} />
      </>
    )
  }

  if (value.kind === "weekCover") {
    const { weeks, bufferUnits, minimumStockLevel, weeklyData, aggregated } = value

    return (
      <>
        <Description>
          Safety stock is set to cover {weeks} weeks of forecasted demand following the purchase
          period.
        </Description>
        <Row label="Weeks of cover" value={`${weeks} weeks`} />
        {minimumStockLevel > 0 && (
          <Row label="Minimum stock" value={`${formatNumber(minimumStockLevel)} units`} />
        )}
        {weeklyData.some((d) => Math.round(d.forecast * 10) > 0) && (
          <WeekCoverBarChart weeklyData={weeklyData} aggregated={aggregated} />
        )}
        <Divider />
        <Row label="Safety stock" value={`${formatNumber(bufferUnits)} units`} />
      </>
    )
  }

  return null
}

export default function SafetyStockTooltip(props: CustomTooltipProps) {
  if (props.location === "header") return <HeaderTooltip {...props} />

  const value = props.value as SafetyStockTooltipValue | undefined
  if (!value) return null

  if (value.kind === "text") {
    return (
      <div className="bg-base shadow-tooltip text-fg-base px-2 py-1.5 rounded-[8px] paragraph-sm whitespace-pre-line">
        {value.text}
      </div>
    )
  }

  if (value.kind === "noSafetyStock") {
    return (
      <Card className="w-[260px]">
        <CardHeader>
          <CardTitle>Safety stock</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1.5 pb-3">
          <SafetyStockContent value={value} />
        </CardContent>
      </Card>
    )
  }

  if (value.kind === "serviceLevel") {
    return (
      <Card className="w-[360px]">
        <CardHeader>
          <CardTitle>Safety stock</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 pb-3">
          <SafetyStockContent value={value} />
        </CardContent>
      </Card>
    )
  }

  if (value.kind === "weekCover") {
    return (
      <Card className="w-[280px]">
        <CardHeader>
          <CardTitle>Safety stock</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 pb-3">
          <SafetyStockContent value={value} />
        </CardContent>
      </Card>
    )
  }

  return null
}
